// ---------------------------------------------------------------------------
// SortableBlock — wraps a block to make it sortable within a page using
// @dnd-kit/sortable. The block can be reordered by dragging its handle.
// ---------------------------------------------------------------------------

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '@/lib/utils'
import { GripVertical } from 'lucide-react'
import type { ReactNode } from 'react'

interface SortableBlockProps {
  /** Block ID — used as sortable id */
  blockId: string
  /** Page ID the block belongs to */
  pageId: string
  /** Index of this block within the page */
  index: number
  /** Human-readable label for the DragOverlay */
  label: string
  /** Whether the editor is in preview mode */
  isPreview: boolean
  children: ReactNode
}

export function SortableBlock({ blockId, pageId, index, label, isPreview, children }: SortableBlockProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: blockId,
    data: {
      type: 'block' as const,
      pageId,
      index,
      label,
    },
    disabled: isPreview,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group/sortable relative',
        isDragging && 'z-50 opacity-50',
      )}
      {...attributes}
    >
      {/* Drag handle — visible on hover in edit mode */}
      {!isPreview && (
        <div
          className="absolute -left-7 top-1/2 -translate-y-1/2 opacity-0 group-hover/sortable:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-20 rounded p-0.5 hover:bg-accent"
          {...listeners}
          title="Drag to reorder"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      )}
      {children}
    </div>
  )
}
